import type { JSX, ReactNode } from 'react';
import type { DossierOuverture, ModificationOuverture } from '../../../types.js';
import formStyles from '../../../styles/form.module.css';

/** Ce qu'une étape envoie : les sections du dossier, sans la révision (l'assistant la pose). */
export type SectionsEnvoi = Omit<ModificationOuverture, 'revision'>;

/** Ce que l'assistant donne à chaque étape. */
export interface PropsEtape {
  dossier: DossierOuverture | null;
  /** Un enregistrement est en cours. */
  enCours: boolean;
  /** Dossier clos, ou rôle qui consulte sans modifier. */
  lectureSeule: boolean;
  /**
   * Enregistre les sections, puis passe à l'étape `suite`. Rend `false` si
   * l'enregistrement a échoué (le message est affiché par l'assistant).
   * `apres` rejoue un geste sur le dossier enregistré (une transition).
   */
  sauver: (
    sections: SectionsEnvoi,
    suite: number,
    apres?: (dossier: DossierOuverture) => Promise<DossierOuverture>,
  ) => Promise<boolean>;
  /** Un dossier à jour (réponse d'un autre appel que l'enregistrement). */
  adopter: (dossier: DossierOuverture) => void;
  /** Relit le dossier côté serveur (après un échec partiel). */
  recharger: () => Promise<void>;
  allerA: (etape: number) => void;
}

/** Un champ de formulaire : libellé, aide, erreur. */
export function Champ({
  id,
  label,
  aide,
  erreur,
  large = false,
  children,
}: {
  id: string;
  label: string;
  aide?: string;
  erreur?: string | undefined;
  large?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className={`${formStyles.champ} ${large ? formStyles.pleineLargeur : ''}`}>
      <label htmlFor={id}>{label}</label>
      {children}
      {aide ? (
        <p id={`${id}-aide`} className={formStyles.aide}>
          {aide}
        </p>
      ) : null}
      {erreur ? (
        <p id={`${id}-erreur`} className={formStyles.erreur} role="alert">
          {erreur}
        </p>
      ) : null}
    </div>
  );
}

/** Les attributs d'accessibilité d'un champ décrit par `Champ`. */
export function descriptionChamp(
  id: string,
  options: { aide?: boolean; erreur?: boolean },
): { 'aria-describedby'?: string; 'aria-invalid'?: boolean } {
  const ids = [options.aide ? `${id}-aide` : '', options.erreur ? `${id}-erreur` : '']
    .filter(Boolean)
    .join(' ');
  return {
    ...(ids ? { 'aria-describedby': ids } : {}),
    ...(options.erreur ? { 'aria-invalid': true } : {}),
  };
}

export const EMAIL_VALIDE = /^\S+@\S+\.\S+$/;
